import {mapTag} from './map-tag';
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {LOCALLY_HANDLED_API_ERROR_CODES} from '../shared/errors/local-api-error';
import {firstValueFrom} from 'rxjs';
import {ApiResponse} from '../auth/auth.models';
import {buildApiUrl} from '../shared/api/api-url';
import {Tag} from './tag';
import {TagAssignmentDto} from './tag-assignment-dto';
import {TagAssignment} from './tag-assignment';
import {TagPageDto} from './tag-page-dto';
import {TagRecordType} from './tag-record-type';
import {TagAuthor} from './tag-author';
import {TagAuthorDto} from './tag-author-dto';

@Injectable({providedIn: 'root'})
export class TagsService {
  private readonly http = inject(HttpClient);
  private readonly url = buildApiUrl('/v1/tags');
  private readonly options = {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['RecordNotFound', 'Conflict', 'BadRequest', 'VALIDATION_ERROR'])};
  async catalog(query = '', offset = 0): Promise<{items: Tag[]; hasMore: boolean}> {
    const response = await firstValueFrom(this.http.get<ApiResponse<TagPageDto>>(this.url, {...this.options, params: {query, offset, limit: 20}}));
    const page = this.data(response);
    return {items: page.items.map(mapTag), hasMore: page.hasMore};
  }
  async assignment(type: TagRecordType, id: string): Promise<TagAssignment> {
    const response = await firstValueFrom(this.http.get<ApiResponse<TagAssignmentDto>>(this.recordUrl(type, id), this.options));
    return this.mapAssignment(this.data(response));
  }
  async save(type: TagRecordType, id: string, revision: string, names: string[]): Promise<TagAssignment> {
    const response = await firstValueFrom(this.http.put<ApiResponse<TagAssignmentDto>>(this.recordUrl(type, id), {revision, names}, this.options));
    return this.mapAssignment(this.data(response));
  }
  async rename(id: string, name: string): Promise<void> {
    this.data(await firstValueFrom(this.http.put<ApiResponse<boolean>>(`${this.url}/${encodeURIComponent(id)}`, {name}, this.options)));
  }
  async remove(id: string): Promise<void> {
    this.data(await firstValueFrom(this.http.delete<ApiResponse<boolean>>(`${this.url}/${encodeURIComponent(id)}`, this.options)));
  }
  async authors(query: string, offset = 0): Promise<TagAuthor[]> {
    const response = await firstValueFrom(this.http.get<ApiResponse<TagAuthorDto[]>>(`${this.url}/authors`, {...this.options, params: {query, offset, limit: 20}}));
    return this.data(response).map(author => ({id: author.id, name: author.name}));
  }
  private recordUrl(type: TagRecordType, id: string): string { return `${this.url}/records/${type}/${encodeURIComponent(id)}`; }
  private mapAssignment(value: TagAssignmentDto): TagAssignment { return {revision: value.revision, tags: value.tags.map(mapTag)}; }
  private data<T>(response: ApiResponse<T>): T {
    if (!response.success || response.data == null) throw new Error('Tag request failed');
    return response.data;
  }
}

